import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, DollarSign, Target, Calendar, Edit2, RefreshCw, Building2, Users, Briefcase, ArrowUpRight, ArrowDownRight, Activity, PieChart, History, HelpCircle, X, Download } from 'lucide-react';
import { getIndustryData } from '../services/api';
import ValuationHistoryModal from '../components/ValuationHistoryModal';
import MetricCard from '../components/dashboard/MetricCard';
import FinancialRatioCard from '../components/dashboard/FinancialRatioCard';
import ValuationForecastChart from '../components/dashboard/ValuationForecastChart';
import EbitdaForecastChart from '../components/dashboard/EbitdaForecastChart';
import { useValuationDashboardData } from '../hooks/useValuationDashboardData';
import {
  formatCurrency,
  formatShortCurrency,
  getMethodDescription,
  calculateFinancialRatios
} from '../utils/valuationDashboardUtils';
import { RATIO_BENCHMARKS, CATEGORY_HELP_CONTENT } from '../utils/valuationDashboardConstants';
import DownloadPDFButton from '../components/DownloadPDFButton';
import { generateForecastData, generateEbitdaForecastData } from '../utils/valuationForecastUtils';

const ValuationDashboard = () => {
  const navigate = useNavigate();

  // Use custom hook for all data management
  const {
    loading,
    latestValuation,
    valuationHistory,
    businessProfile,
    wealthGap,
    valueGap,
    profitGap,
    industryData,
    editMode,
    showHistoryModal,
    viewingHistoricalValuation,
    valuationType,
    growthRate,
    profitMargin,
    ebitdaMargin,
    industryMultiple,
    discountRate,
    netWorthGrowthRate,
    exitValueGrowthRate,
    wealthGapGrowthRate,
    financials,
    goals,
    setEditMode,
    setShowHistoryModal,
    setViewingHistoricalValuation,
    setGrowthRate,
    setProfitMargin,
    setEbitdaMargin,
    setIndustryMultiple,
    setDiscountRate,
    setNetWorthGrowthRate,
    setExitValueGrowthRate,
    setWealthGapGrowthRate,
    setFinancials,
    setGoals,
    fetchAllData,
    handleToggleValuationType,
    handleBackToCurrent,
    handleDeleteValuation,
    handleLoadHistoricalValuation
  } = useValuationDashboardData();

  // Local UI state
  const [showHelpModal, setShowHelpModal] = useState(null);

  // Forecast Controls always show Growth Rate and EBITDA Margin for all methods
  const visibleSliders = {
    growthRate: true,
    ebitdaMargin: true,
    industryMultiple: false,
    discountRate: false
  };

  // Financial Ratio Help Modal State
  const [showRatioHelpModal, setShowRatioHelpModal] = useState(false);
  const [ratioHelpContent, setRatioHelpContent] = useState(null);

  // Custom display names for ratios that need proper capitalization
  const ratioDisplayNames = {
    'ebitdaMargin': 'EBITDA Margin',
    'roa': 'ROA',
    'roe': 'ROE'
  };

  // Function to show help modal for a specific ratio
  const showRatioHelp = (ratioName) => {
    const benchmark = RATIO_BENCHMARKS[ratioName];
    if (benchmark) {
      setRatioHelpContent({ name: ratioName, ...benchmark });
      setShowRatioHelpModal(true);
    }
  };

  // Function to show category help modal
  const showCategoryHelp = (category) => {
    const content = CATEGORY_HELP_CONTENT[category];
    if (content) {
      setRatioHelpContent({ ...content, isCategory: true });
      setShowRatioHelpModal(true);
    }
  };

  // Calculate financial ratios
  const ratios = calculateFinancialRatios(financials, latestValuation);

  // Generate forecast data using utility functions
  const forecastData = generateForecastData({
    latestValuation,
    growthRate
  });

  const ebitdaForecastData = generateEbitdaForecastData({
    financials,
    growthRate,
    ebitdaMargin,
    profitGap
  });

  // Calculate wealth gap difference for legacy display
  const wealthGapDifference = wealthGap && wealthGap.calculated_wealth_gap !== undefined
    ? wealthGap.calculated_wealth_gap
    : (latestValuation && goals.targetValuation
        ? goals.targetValuation - latestValuation.valuation_amount
        : 0);

  const valueGapPercentage = goals.targetValuation && goals.targetValuation > 0
    ? Math.round((Math.abs(wealthGapDifference) / goals.targetValuation) * 100)
    : 0;

  // Prepare history chart data
  const historyChartData = valuationHistory.slice().reverse().map((val, index) => ({
    date: new Date(val.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    valuation: val.valuation_amount,
    lowRange: val.low_range,
    highRange: val.high_range
  }));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!latestValuation) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Valuation Type Toggle and Actions */}
        <div className="mb-6 flex justify-center items-center gap-4">
          {/* Toggle */}
          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
            <button
              onClick={() => handleToggleValuationType('simple')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                valuationType === 'simple'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Simple Valuation
            </button>
            <button
              onClick={() => handleToggleValuationType('advanced')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                valuationType === 'advanced'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Advanced Valuation
            </button>
          </div>

          {/* History Button */}
          {valuationHistory.length > 0 && (
            <button
              onClick={() => setShowHistoryModal(true)}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 font-medium text-gray-700"
            >
              <History size={18} />
              History
            </button>
          )}

          {/* Revert to Latest Button */}
          {viewingHistoricalValuation && (
            <button
              onClick={() => {
                setViewingHistoricalValuation(false);
                fetchAllData();
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 font-medium"
            >
              <RefreshCw size={18} />
              Revert to Latest
            </button>
          )}
        </div>

        {/* Empty State */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No {valuationType === 'simple' ? 'Simple' : 'Advanced'} Valuations Yet
          </h2>
          <p className="text-gray-600 mb-4">
            {valuationType === 'simple'
              ? 'Calculate your first simple valuation to see your dashboard'
              : 'Calculate your first advanced valuation with multi-year financial data'}
          </p>
          <a
            href={valuationType === 'simple' ? '/valuation' : '/valuation'}
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Create {valuationType === 'simple' ? 'Simple' : 'Advanced'} Valuation
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {/* Valuation Type Toggle */}
      <div className="mb-6 flex justify-center">
        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
          <button
            onClick={() => handleToggleValuationType('simple')}
            className={`px-4 sm:px-6 py-2 rounded-md font-medium transition-all text-sm sm:text-base min-h-[44px] ${
              valuationType === 'simple'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Simple<span className="hidden sm:inline"> Valuation</span>
          </button>
          <button
            onClick={() => handleToggleValuationType('advanced')}
            className={`px-4 sm:px-6 py-2 rounded-md font-medium transition-all text-sm sm:text-base min-h-[44px] ${
              valuationType === 'advanced'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Advanced<span className="hidden sm:inline"> Valuation</span>
          </button>
        </div>
      </div>

      {/* Historical Valuation Banner */}
      {viewingHistoricalValuation && (
        <div className="mb-4 bg-amber-50 border-2 border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calendar className="text-amber-600" size={24} />
            <div>
              <h3 className="font-semibold text-amber-900">Viewing Historical Valuation</h3>
              <p className="text-sm text-amber-700">
                Calculated on {new Date(latestValuation.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <button
            onClick={handleBackToCurrent}
            className="flex items-center space-x-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition"
          >
            <RefreshCw size={18} />
            <span>Back to Current</span>
          </button>
        </div>
      )}

      {/* Dashboard Content - Wrapped for PDF Generation */}
      <div id="valuation-dashboard-content" className="pdf-section">
        {/* Header with Business Info */}
        <div className="mb-5 pdf-header-section">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Valuation Dashboard</h1>
            <p className="text-sm text-gray-500">Track your estimated business exit range of value and forecast growth</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            {latestValuation && businessProfile && (
              <DownloadPDFButton
                type="valuation"
                subType={valuationType}
                businessId={businessProfile.id}
                label="Download PDF"
                className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg text-sm font-medium min-h-[44px]"
                forecastParams={{
                  growthRate,
                  ebitdaMargin,
                  years: 5,  // Default 5-year forecast
                  confidenceRange: 25  // Default ±25% confidence range
                }}
              />
            )}
            {valuationHistory.length > 0 && (
              <button
                onClick={() => setShowHistoryModal(true)}
                className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg text-sm font-medium min-h-[44px]"
              >
                <History size={18} />
                <span>History</span>
              </button>
            )}
          </div>
        </div>

        {businessProfile && (
          <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 p-3 sm:p-4 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-2">
              <Building2 size={16} className="text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Business</p>
                <p className="text-sm font-semibold text-gray-900">{businessProfile.business_name}</p>
              </div>
            </div>
            <div className="h-8 w-px bg-blue-200"></div>
            <div className="flex items-center gap-2">
              <Briefcase size={16} className="text-indigo-600" />
              <div>
                <p className="text-xs text-gray-500">Industry</p>
                <p className="text-sm font-semibold text-gray-900">{businessProfile.industry}</p>
              </div>
            </div>
            {businessProfile.employees && (
              <>
                <div className="h-8 w-px bg-blue-200"></div>
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-purple-600" />
                  <div>
                    <p className="text-xs text-gray-500">Employees</p>
                    <p className="text-sm font-semibold text-gray-900">{businessProfile.employees}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Key Metrics Cards - Top Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 pdf-valuation-card">
        {/* Current Valuation */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 rounded-xl shadow-lg p-5 text-white group hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-white/20 rounded-lg backdrop-blur-sm">
                <DollarSign size={20} />
              </div>
              <ArrowUpRight size={18} className="text-blue-200 opacity-70" />
            </div>
            <p className="text-blue-100 text-xs font-medium mb-1">Current Median Business Exit Value</p>
            <p className="text-2xl font-bold mb-1">{formatShortCurrency(latestValuation?.valuation_amount || 0)}</p>
            {latestValuation?.low_range && latestValuation?.high_range && (
              <p className="text-blue-200 text-xs opacity-90">
                {formatShortCurrency(latestValuation.low_range)} - {formatShortCurrency(latestValuation.high_range)}<span className="text-blue-200 text-xs opacity-90"> (Range of Exit Value)</span>
              </p>
            )}
          </div>
        </div>

        {/* Revenue */}
        <MetricCard
          icon={TrendingUp}
          label="Revenue"
          title="Annual Revenue"
          value={formatShortCurrency(financials.revenue)}
          supplementaryText={businessProfile?.growth_rate && (
            <span className="flex items-center">
              <ArrowUpRight size={14} className="mr-1" />
              {businessProfile.growth_rate}% Growth
            </span>
          )}
          iconGradient="from-green-100 to-emerald-100"
          labelColor="green"
          labelBgColor="green"
          borderHoverColor="green"
        />

        {/* EBITDA */}
        <MetricCard
          icon={Activity}
          label="EBITDA"
          title="Annual EBITDA"
          value={formatShortCurrency(financials.ebitda)}
          supplementaryText={financials.grossMargin > 0 && `${financials.grossMargin}% Margin`}
          iconGradient="from-purple-100 to-violet-100"
          labelColor="purple"
          labelBgColor="purple"
          borderHoverColor="purple"
        />

        {/* Net Income */}
        <MetricCard
          icon={DollarSign}
          label="Net Income"
          title="Net Income"
          value={formatShortCurrency(financials.netIncome)}
          supplementaryText={financials.profitMargin > 0 && `${financials.profitMargin}% Margin`}
          iconGradient="from-indigo-100 to-blue-100"
          labelColor="indigo"
          labelBgColor="indigo"
          borderHoverColor="indigo"
        />
      </div>

      {/* Valuation Method - Full Width Horizontal */}
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-100 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <PieChart size={20} className="text-blue-600" />
            </div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-700">Valuation Method</h2>
          </div>
          <button
            onClick={() => navigate('/valuation')}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm min-h-[44px] w-full sm:w-auto"
          >
            New Valuation
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 pdf-metrics-section">
          {/* Primary Method */}
          <div className="col-span-2 md:col-span-1">
            <p className="text-sm text-gray-600 mb-2">Primary Method</p>
            <p className="text-base font-bold text-gray-900 leading-tight">
              {latestValuation?.method === 'cca' ? 'Comparable Company Analysis' :
               latestValuation?.method === 'dcf' ? 'Discounted Cash Flow' :
               latestValuation?.method === 'coe' ? 'Capitalization of Earnings' :
               latestValuation?.method === 'nav' ? 'Net Asset Value' :
               latestValuation?.method === 'comprehensive' ? 'Comprehensive Analysis' :
               latestValuation?.method === 'manual' ? 'Manual Multiple' :
               latestValuation?.method || 'Multiple of EBITDA'}
            </p>
            {(() => {
              try {
                const calcDetails = latestValuation?.calculation_details ?
                  (typeof latestValuation.calculation_details === 'string' ?
                    JSON.parse(latestValuation.calculation_details) :
                    latestValuation.calculation_details) : null;

                if (calcDetails?.primary_method) {
                  return (
                    <p className="text-xs text-gray-500 mt-2">
                      {calcDetails.primary_method}
                    </p>
                  );
                }
              } catch (e) {
                console.error('Error parsing calculation_details:', e);
              }
              return null;
            })()}
          </div>

          {/* Industry */}
          {industryData && (
            <div className="col-span-2 md:col-span-1">
              <p className="text-sm text-gray-600 mb-2">Industry</p>
              <p className="text-base font-semibold text-gray-900 leading-tight">
                {industryData.industry_name}
              </p>
            </div>
          )}

          {/* Private Company Discount */}
          {(() => {
            try {
              const inputData = latestValuation?.input_data ?
                (typeof latestValuation.input_data === 'string' ?
                  JSON.parse(latestValuation.input_data) :
                  latestValuation.input_data) : null;

              if (inputData?.private_company_discount !== undefined) {
                const discount = inputData.private_company_discount * 100;
                return (
                  <div className="col-span-2 md:col-span-1">
                    <p className="text-sm text-gray-600 mb-2">Private Discount</p>
                    <p className="text-base font-semibold text-purple-600">
                      {discount.toFixed(0)}%
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Liquidity adjustment
                    </p>
                  </div>
                );
              }
            } catch (e) {
              console.error('Error parsing input_data for discount:', e);
            }
            return null;
          })()}

          {/* Valuation Range */}
          <div className="col-span-2 md:col-span-1">
            <p className="text-sm text-gray-600 mb-2">Range of Exit Value</p>
            <p className="text-sm font-semibold text-gray-900 leading-relaxed">
              {formatShortCurrency(latestValuation?.low_range || 0)} - {formatShortCurrency(latestValuation?.high_range || 0)}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Uncertainty range
            </p>
          </div>

          {/* Valuation Date */}
          <div className="col-span-2 md:col-span-1">
            <p className="text-sm text-gray-600 mb-2">Valuation Date</p>
            <p className="text-sm font-semibold text-gray-900">
              {latestValuation ? new Date(latestValuation.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              }) : 'N/A'}
            </p>
          </div>

          {/* Exit Horizon */}
          {businessProfile?.exit_horizon && (
            <div className="col-span-2 md:col-span-1">
              <p className="text-sm text-gray-600 mb-2">Exit Horizon</p>
              <p className="text-sm font-semibold text-gray-900">{businessProfile.exit_horizon}</p>
            </div>
          )}
        </div>
      </div>

      {/* Current Estimated Exit Value Section - Show for both Simple and Advanced */}
      {valueGap && latestValuation && latestValuation.low_range && latestValuation.high_range && (
        (() => {
          // Use the actual valuation range from the selected valuation type
          const valuationLow = latestValuation.low_range;
          const valuationHigh = latestValuation.high_range;
          const valuationRange = valuationHigh - valuationLow;

          // Calculate valuation position using the assessment score
          const valuationPosition = ((valueGap.current_attractiveness - 17) / 83) * 100;

          // Calculate current exit value based on actual valuation range
          const currentExitValue = valuationLow + (valuationRange * (valuationPosition / 100));

          return (
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <TrendingUp size={20} className="text-emerald-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-700">Current Estimated Exit Value</h2>
              </div>

              {/* Top Row: Assessment Score Scaling - Spans 2 columns */}
              <div className="grid grid-cols-1 gap-4 mb-4">
                <div className="col-span-1 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <p className="text-base text-indigo-900 font-semibold mb-3">Assessment Score Scaling (17% - 100%)</p>
                  <p className="text-sm text-indigo-800">
                    Assessment scores range from <span className="font-bold">17% (minimum)</span> to <span className="font-bold">100% (maximum)</span>.
                    A <span className="font-bold">58% assessment</span> represents an average business at approximately the <span className="font-bold">midpoint (49%)</span> of your valuation range.
                  </p>
                  <p className="text-sm text-indigo-800 mt-1">
                    Linear scaling: 17% → 0% (low), 58% → 49% (midpoint), 67% → 60% (green zone), 72% → 66% (best-in-class), 100% → 100% (high)
                  </p>
                </div>
              </div>

              {/* Second Row: Step 1 and Step 2 side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Step 1: Scale Current Assessment Score */}
                <div className="bg-gray-50 border-l-4 border-gray-500 p-4 rounded">
                  <p className="text-base font-semibold text-gray-900 mb-3">Step 1: Scale Current Overall Business Gap Assessment Score</p>
                  <p className="text-sm text-gray-800">
                    Valuation Position = (Assessment Score - 17) / 83 × 100
                  </p>
                  <p className="text-sm text-gray-800 mt-1">
                    = ({valueGap.current_attractiveness?.toFixed(1)}% - 17) / 83 × 100
                  </p>
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    = {valuationPosition.toFixed(1)}%
                  </p>
                </div>

                {/* Step 2: Calculate Current Exit Value */}
                <div className="bg-gray-50 border-l-4 border-gray-500 p-4 rounded">
                  <p className="text-base font-semibold text-gray-900 mb-3">Step 2: Calculate Current Estimated Business Exit Value</p>
                  <p className="text-sm text-gray-800">
                    Current Exit Value = Low + (Range × Valuation Position)
                  </p>
                  <p className="text-sm text-gray-800 mt-2">
                    = {formatCurrency(valuationLow)} + ({formatCurrency(valuationRange)} × {valuationPosition.toFixed(1)}%)
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    = {formatCurrency(currentExitValue)}
                  </p>
                </div>
              </div>
            </div>
          );
        })()
      )}

      {/* Financial Ratios - Full Width Horizontal - Only show for Advanced Valuation */}
      {valuationType === 'advanced' && (
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-100 mb-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-green-50 rounded-lg">
            <Activity size={20} className="text-green-600" />
          </div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-700">Financial Ratios</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-9 gap-4 pdf-ratios-section">
          {/* Profitability Ratios */}
          <div className="col-span-1 sm:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">💰 Profitability</h3>
              <button
                onClick={() => showCategoryHelp('profitability')}
                className="text-gray-500 hover:text-blue-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <HelpCircle size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Gross Margin */}
              <FinancialRatioCard
                ratioName="grossMargin"
                value={ratios.grossMargin}
                unit="%"
                category="profitability"
                onHelpClick={showRatioHelp}
              />

              {/* EBITDA Margin */}
              <FinancialRatioCard
                ratioName="ebitdaMargin"
                value={ratios.ebitdaMargin}
                unit="%"
                category="profitability"
                onHelpClick={showRatioHelp}
              />
            </div>
          </div>

          {/* Liquidity Ratios */}
          <div className="col-span-1 sm:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">💧 Liquidity</h3>
              <button
                onClick={() => showCategoryHelp('liquidity')}
                className="text-gray-500 hover:text-blue-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <HelpCircle size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Current Ratio */}
              <FinancialRatioCard
                ratioName="currentRatio"
                value={ratios.currentRatio}
                unit="x"
                category="liquidity"
                onHelpClick={showRatioHelp}
              />

              {/* Cash Ratio */}
              <FinancialRatioCard
                ratioName="cashRatio"
                value={ratios.cashRatio}
                unit="x"
                category="liquidity"
                onHelpClick={showRatioHelp}
              />
            </div>
          </div>

          {/* Leverage Ratios */}
          <div className="col-span-1 sm:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">⚖️ Leverage</h3>
              <button
                onClick={() => showCategoryHelp('leverage')}
                className="text-gray-500 hover:text-blue-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <HelpCircle size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Debt-to-Equity */}
              <FinancialRatioCard
                ratioName="debtToEquity"
                value={ratios.debtToEquity}
                unit="x"
                category="leverage"
                onHelpClick={showRatioHelp}
              />

              {/* Debt-to-Assets */}
              <FinancialRatioCard
                ratioName="debtToAssets"
                value={ratios.debtToAssets}
                unit="%"
                category="leverage"
                onHelpClick={showRatioHelp}
              />
            </div>
          </div>

          {/* Efficiency Ratios */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-4 xl:col-span-3">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">⚡ Efficiency</h3>
              <button
                onClick={() => showCategoryHelp('efficiency')}
                className="text-gray-500 hover:text-blue-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <HelpCircle size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Asset Turnover */}
              <FinancialRatioCard
                ratioName="assetTurnover"
                value={ratios.assetTurnover}
                unit="x"
                category="efficiency"
                onHelpClick={showRatioHelp}
              />

              {/* ROA */}
              <FinancialRatioCard
                ratioName="roa"
                value={ratios.roa}
                unit="%"
                category="efficiency"
                onHelpClick={showRatioHelp}
              />

              {/* ROE */}
              <FinancialRatioCard
                ratioName="roe"
                value={ratios.roe}
                unit="%"
                category="efficiency"
                onHelpClick={showRatioHelp}
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Growth Metrics (CAGR) - Only show for Advanced Valuation */}
      {(() => {
        if (valuationType !== 'advanced' || !latestValuation?.calculation_details) {
          return null;
        }

        try {
          const calcDetails = typeof latestValuation.calculation_details === 'string'
            ? JSON.parse(latestValuation.calculation_details)
            : latestValuation.calculation_details;

          const growthMetrics = calcDetails?.growth_metrics;

          if (!growthMetrics) {
            return null;
          }

          return (
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-100 mb-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <TrendingUp size={20} className="text-blue-600" />
                </div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-700">Growth Metrics</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {/* Revenue CAGR */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Revenue CAGR</p>
                  </div>
                  <p className="text-3xl font-bold text-blue-600 mb-1">
                    {growthMetrics.revenue_cagr.toFixed(1)}%
                  </p>
                  <p className="text-xs text-blue-700">Annual Growth Rate</p>
                </div>

                {/* EBITDA CAGR */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs font-semibold text-purple-800 uppercase tracking-wide">EBITDA CAGR</p>
                  </div>
                  <p className="text-3xl font-bold text-purple-600 mb-1">
                    {growthMetrics.ebitda_cagr.toFixed(1)}%
                  </p>
                  <p className="text-xs text-purple-700">Profitability Growth</p>
                </div>

                {/* SDE CAGR */}
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">SDE CAGR</p>
                  </div>
                  <p className="text-3xl font-bold text-amber-600 mb-1">
                    {growthMetrics.sde_cagr.toFixed(1)}%
                  </p>
                  <p className="text-xs text-amber-700">Owner Earnings Growth</p>
                </div>
              </div>
            </div>
          );
        } catch (e) {
          console.error('Error parsing growth_metrics from calculation_details:', e);
          return null;
        }
      })()}

      {/* Financial Trends Charts - Only show for Advanced Valuation */}
      {(() => {
        if (valuationType !== 'advanced' || !latestValuation?.input_data) {
          return null;
        }

        try {
          const inputData = typeof latestValuation.input_data === 'string'
            ? JSON.parse(latestValuation.input_data)
            : latestValuation.input_data;

          // Extract years from the revenue field
          const years = inputData.revenue ? Object.keys(inputData.revenue).sort() : [];

          if (years.length === 0) {
            return null;
          }

          // Prepare Income Statement chart data
          const incomeStatementData = years.map(year => ({
            year: year.toString(),
            'Revenue': parseFloat(inputData.revenue?.[year] || 0),
            'Gross Profit': parseFloat(inputData.gross_profit?.[year] || 0),
            'EBITDA': parseFloat(inputData.ebitda?.[year] || 0),
            'SDE': parseFloat(inputData.sde?.[year] || 0),
          }));

          // Prepare Balance Sheet chart data
          const balanceSheetData = years.map(year => ({
            year: year.toString(),
            'Total Assets': parseFloat(inputData.total_assets?.[year] || 0),
            'Total Liabilities': parseFloat(inputData.total_liabilities?.[year] || 0),
            'Total Equity': parseFloat(inputData.equity?.[year] || 0),
          }));

          const formatCurrency = (value) => {
            if (!value) return '$0';
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
          };

          return (
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-100 mb-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <PieChart size={20} className="text-purple-600" />
                </div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-700">Financial Trends</h2>
              </div>

              {/* Income Statement Chart */}
              <div className="mb-8 bg-gray-50 border rounded-lg p-4 sm:p-6">
                <h4 className="text-md font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="text-blue-600" size={20} />
                  Income Statement Trends
                </h4>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={incomeStatementData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} />
                    <Tooltip
                      formatter={(v) => formatCurrency(v)}
                      itemSorter={(item) => {
                        const order = ['Revenue', 'Gross Profit', 'EBITDA', 'SDE'];
                        return order.indexOf(item.name);
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                      content={(props) => {
                        const items = [
                          { value: 'Revenue', color: '#f97316' },
                          { value: 'Gross Profit', color: '#10b981' },
                          { value: 'EBITDA', color: '#3b82f6' },
                          { value: 'SDE', color: '#9333ea' }
                        ];
                        return (
                          <ul style={{ display: 'flex', justifyContent: 'center', listStyle: 'none', padding: 0, margin: 0, gap: '20px' }}>
                            {items.map((item, index) => (
                              <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <svg width="20" height="14" style={{ display: 'inline-block' }}>
                                  <line x1="0" y1="7" x2="20" y2="7" stroke={item.color} strokeWidth="2" />
                                </svg>
                                <span style={{ fontSize: '14px' }}>{item.value}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }}
                    />
                    <Line type="monotone" dataKey="Revenue" stroke="#f97316" strokeWidth={2} />
                    <Line type="monotone" dataKey="Gross Profit" stroke="#10b981" strokeWidth={2} />
                    <Line type="monotone" dataKey="EBITDA" stroke="#3b82f6" strokeWidth={2} />
                    <Line type="monotone" dataKey="SDE" stroke="#9333ea" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Balance Sheet Chart */}
              <div className="bg-gray-50 border rounded-lg p-4 sm:p-6">
                <h4 className="text-md font-semibold mb-4 flex items-center gap-2">
                  <Activity className="text-green-600" size={20} />
                  Balance Sheet Trends
                </h4>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={balanceSheetData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} />
                    <Tooltip
                      formatter={(v) => formatCurrency(v)}
                      itemSorter={(item) => {
                        const order = ['Total Assets', 'Total Liabilities', 'Total Equity'];
                        return order.indexOf(item.name);
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                      content={(props) => {
                        const items = [
                          { value: 'Total Assets', color: '#3b82f6' },
                          { value: 'Total Liabilities', color: '#ef4444' },
                          { value: 'Total Equity', color: '#10b981' }
                        ];
                        return (
                          <ul style={{ display: 'flex', justifyContent: 'center', listStyle: 'none', padding: 0, margin: 0, gap: '20px' }}>
                            {items.map((item, index) => (
                              <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <svg width="20" height="14" style={{ display: 'inline-block' }}>
                                  <line x1="0" y1="7" x2="20" y2="7" stroke={item.color} strokeWidth="2" />
                                </svg>
                                <span style={{ fontSize: '14px' }}>{item.value}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }}
                    />
                    <Line type="monotone" dataKey="Total Assets" stroke="#3b82f6" strokeWidth={2} />
                    <Line type="monotone" dataKey="Total Liabilities" stroke="#ef4444" strokeWidth={2} />
                    <Line type="monotone" dataKey="Total Equity" stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        } catch (e) {
          console.error('Error preparing financial trends charts:', e);
          return null;
        }
      })()}

      {/* Forecast Controls - Full Width Horizontal */}
      <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl shadow-md p-4 sm:p-5 border border-gray-200 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg">
            <TrendingUp size={18} className="text-green-600" />
          </div>
          <h2 className="text-sm sm:text-base font-semibold text-gray-800">Forecast Controls</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {/* Growth Rate Slider - Always visible */}
          {visibleSliders.growthRate && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Annual Growth Rate</label>
                  <button
                    onClick={() => setShowHelpModal('growthRate')}
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                    title="Learn more about Annual Growth Rate"
                  >
                    <HelpCircle size={16} />
                  </button>
                </div>
                <span className="text-lg font-bold text-green-600 bg-green-50 px-3 py-1 rounded-lg shadow-sm">{growthRate}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="200"
                value={growthRate}
                onChange={(e) => setGrowthRate(parseInt(e.target.value))}
                className="w-full h-2.5 rounded-full appearance-none cursor-pointer transition-all"
                style={{
                  background: `linear-gradient(to right, #10b981 0%, #10b981 ${growthRate / 2}%, #e5e7eb ${growthRate / 2}%, #e5e7eb 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                <span>0%</span>
                <span>200%</span>
              </div>
            </div>
          )}

          {/* EBITDA Margin Slider - CCA and Capitalization only */}
          {visibleSliders.ebitdaMargin && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">EBITDA Margin</label>
                  <button
                    onClick={() => setShowHelpModal('ebitdaMargin')}
                    className="text-gray-400 hover:text-purple-600 transition-colors"
                    title="Learn more about EBITDA Margin"
                  >
                    <HelpCircle size={16} />
                  </button>
                </div>
                <span className="text-lg font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-lg shadow-sm">{ebitdaMargin}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={ebitdaMargin}
                onChange={(e) => setEbitdaMargin(parseInt(e.target.value))}
                className="w-full h-2.5 rounded-full appearance-none cursor-pointer transition-all"
                style={{
                  background: `linear-gradient(to right, #9333ea 0%, #9333ea ${ebitdaMargin}%, #e5e7eb ${ebitdaMargin}%, #e5e7eb 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                <span>0%</span>
                <span>100%</span>
              </div>
            </div>
          )}

          {/* Industry Multiple Slider - CCA and Manual only */}
          {visibleSliders.industryMultiple && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Industry Multiple</label>
                <span className="text-lg font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg shadow-sm">{industryMultiple}x</span>
              </div>
              <input
                type="range"
                min="1"
                max="30"
                step="0.5"
                value={industryMultiple}
                onChange={(e) => setIndustryMultiple(parseFloat(e.target.value))}
                className="w-full h-2.5 rounded-full appearance-none cursor-pointer transition-all"
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(industryMultiple / 30) * 100}%, #e5e7eb ${(industryMultiple / 30) * 100}%, #e5e7eb 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                <span>1x</span>
                <span>30x</span>
              </div>
            </div>
          )}

          {/* Discount Rate Slider - DCF only */}
          {visibleSliders.discountRate && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Discount Rate (WACC)</label>
                <span className="text-lg font-bold text-orange-600 bg-orange-50 px-3 py-1 rounded-lg shadow-sm">{discountRate}%</span>
              </div>
              <input
                type="range"
                min="5"
                max="30"
                step="0.5"
                value={discountRate}
                onChange={(e) => setDiscountRate(parseFloat(e.target.value))}
                className="w-full h-2.5 rounded-full appearance-none cursor-pointer transition-all"
                style={{
                  background: `linear-gradient(to right, #f97316 0%, #f97316 ${((discountRate - 5) / 25) * 100}%, #e5e7eb ${((discountRate - 5) / 25) * 100}%, #e5e7eb 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                <span>5%</span>
                <span>30%</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-center">
          <button
            onClick={fetchAllData}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-medium shadow-md hover:shadow-lg text-sm min-h-[44px] w-full sm:w-auto"
          >
            <RefreshCw size={16} />
            Refresh Data
          </button>
        </div>
      </div>

      {/* Charts Grid - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 pdf-forecast-section">
        {/* Chart 1: Valuation Growth Forecast */}
        <ValuationForecastChart
          forecastData={forecastData}
          growthRate={growthRate}
          latestValuation={latestValuation}
        />

        {/* Chart 2: Revenue and EBITDA Forecast */}
        <EbitdaForecastChart
          ebitdaForecastData={ebitdaForecastData}
          growthRate={growthRate}
          ebitdaMargin={ebitdaMargin}
          profitGap={profitGap}
        />
      </div>

      {/* Valuation History Modal */}
      {/* Ratio Help Modal */}
      {showRatioHelpModal && ratioHelpContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 flex justify-between items-center shadow-lg rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                  <HelpCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">
                    {ratioHelpContent.isCategory ? ratioHelpContent.title :
                     ratioDisplayNames[ratioHelpContent.name] ||
                     ratioHelpContent.name.split(/(?=[A-Z])/).join(' ').replace(/^\w/, c => c.toUpperCase())}
                  </h3>
                  <p className="text-blue-100 text-sm">
                    {ratioHelpContent.isCategory ? 'Category Information' : 'Financial Ratio Details'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRatioHelpModal(false)}
                className="text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {ratioHelpContent.isCategory ? (
                // Category Help Content
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">Description</h4>
                    <p className="text-gray-700">{ratioHelpContent.description}</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">Key Metrics</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {ratioHelpContent.keyMetrics.map((metric, idx) => (
                        <li key={idx} className="text-gray-700">{metric}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">Interpretation</h4>
                    <p className="text-gray-700">{ratioHelpContent.interpretation}</p>
                  </div>
                </div>
              ) : (
                // Individual Ratio Help Content
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">Description</h4>
                    <p className="text-gray-700">{ratioHelpContent.description}</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">Calculation</h4>
                    <p className="font-mono text-sm bg-gray-100 p-3 rounded-lg text-gray-900">
                      {ratioHelpContent.calculation}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">Benchmarks</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-green-800 mb-1">Excellent</p>
                        <p className="text-lg font-bold text-green-700">
                          {ratioHelpContent.higher ? '≥' : '≤'} {ratioHelpContent.good}{ratioHelpContent.unit}
                        </p>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-yellow-800 mb-1">Acceptable</p>
                        <p className="text-lg font-bold text-yellow-700">
                          {ratioHelpContent.higher ? '≥' : '≤'} {ratioHelpContent.acceptable}{ratioHelpContent.unit}
                        </p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-red-800 mb-1">Needs Attention</p>
                        <p className="text-lg font-bold text-red-700">
                          {ratioHelpContent.higher ? '<' : '>'} {ratioHelpContent.acceptable}{ratioHelpContent.unit}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">Interpretation</h4>
                    <p className="text-gray-700">{ratioHelpContent.interpretation}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 bg-gray-50 p-4">
              <button
                onClick={() => setShowRatioHelpModal(false)}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 px-4 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      {/* End Dashboard Content Wrapper */}

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  {showHelpModal === 'growthRate' && 'Annual Growth Rate'}
                  {showHelpModal === 'ebitdaMargin' && 'EBITDA Margin'}
                </h3>
                <button
                  onClick={() => setShowHelpModal(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4 text-gray-700">
                {showHelpModal === 'growthRate' && (
                  <>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">What it measures:</h4>
                      <p>The Annual Growth Rate controls how fast your business valuation is projected to grow over the next 5 years.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">How it works:</h4>
                      <p>This rate is applied quarterly to your current business valuation, showing you the projected value trajectory. For example, a 15% annual growth rate means your business value could increase by 15% each year if current trends continue.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">What to consider:</h4>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Industry average growth rates (typically 5-20% for healthy businesses)</li>
                        <li>Your company's historical growth performance</li>
                        <li>Market conditions and economic outlook</li>
                        <li>Planned investments or expansion initiatives</li>
                        <li>Competitive landscape and market share potential</li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm"><strong>Tip:</strong> Conservative estimates (10-15%) are safer for exit planning, while aggressive estimates (20%+) should be backed by strong market data and growth strategies.</p>
                    </div>
                  </>
                )}

                {showHelpModal === 'ebitdaMargin' && (
                  <>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">What it measures:</h4>
                      <p>EBITDA (Earnings Before Interest, Taxes, Depreciation, and Amortization) Margin shows what percentage of your revenue becomes operating profit.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">How it works:</h4>
                      <p>This slider adjusts your projected EBITDA as a percentage of revenue. The chart shows your current EBITDA (based on this margin) and the profit gap to reach industry-leading margins. Higher EBITDA margins indicate better operational efficiency and profitability.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">What to consider:</h4>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Industry benchmarks (margins vary widely by sector)</li>
                        <li>Your company's historical EBITDA performance</li>
                        <li>Operational efficiency improvements you can implement</li>
                        <li>Cost reduction opportunities</li>
                        <li>The gap between your current margin and industry leaders</li>
                      </ul>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <p className="text-sm"><strong>Tip:</strong> Improving EBITDA margin even by a few percentage points can significantly increase your business valuation. Focus on operational efficiency and cost management.</p>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowHelpModal(null)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ValuationHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        valuations={valuationHistory}
        onLoad={handleLoadHistoricalValuation}
        onDelete={handleDeleteValuation}
        valuationType={valuationType}
      />
    </div>
  );
};

export default ValuationDashboard;
